// Helpers for working with offers, suppliers and L4B price agreements
// returned by /hotel/gethotelwithprices.
//
// Data shape (relevant fields):
//   hotel.suppliers[] = [{ name: "OpenGds" | "ExpediaRapid"
//                                | "LookingforBooking agreed prices",
//                          offers: [...] | null }]
//   hotel.expediaRapidRooms[] = [{ roomId, roomName, internalRoomId,
//                                  offers: [{ reservationType:
//                                  "ExpediaRapid"|"OpenGds", ... }] }]
//   hotel.hotelAgreedPrices[] = [{ priceType: "OpenGdsFixed"
//                                            |"OpenGdsPercentageOfBar"
//                                            |"OpenGdsExclusive"
//                                            |"Bar"
//                                            | ... (any future type),
//                                  percentageOfBar, expediaRoomId,
//                                  openGdsAccommodationId, accommodationName,
//                                  refundable, cancellationPolicy, price,
//                                  ... }]
//
//   priceType meanings:
//     OpenGdsFixed           – fixed agreed price; nightly = agreement.price.
//                              Applies to one room (matched by expediaRoomId).
//     OpenGdsPercentageOfBar – nightly = cheapestExpedia.nightlyTotal × pct.
//                              Applies to one room (matched by expediaRoomId).
//     OpenGdsExclusive       – fixed agreed price; nightly = agreement.price
//                              (same calculation as OpenGdsFixed). Applies
//                              to one room (matched by expediaRoomId).
//     Bar                    – hotel-wide flag. No price override; show
//                              Expedia rates as-is. Forces CTA to
//                              "reservering aanvragen" (direct booking is
//                              disabled hotel-wide).
//
//   IMPORTANT: When the API delivers an OpenGds offer inside
//   `expediaRapidRooms[i].offers[]`, the offer is ALREADY priced (by the
//   backend, applying the agreement). It is **direct bookable** — CTA
//   "nu boeken", reservationSystem=1 (direct booking via OpenGds).
//   Synthesis only happens as a FALLBACK when the agreement targets a room
//   that has no OpenGds availability (only Expedia rates). Synthesized
//   (fallback) offers carry `isSyntheticAgreedPrice: true` — CTA
//   "reservering aanvragen", reservationSystem=0 (booking request sent
//   to Salesforce, not auto-booked).
//
//   AGREEMENT-HOTEL POLICY: when ANY agreement (Bar or OpenGds variants)
//   exists on a hotel, Expedia offers on that hotel are also routed
//   through the request flow — we never push an Expedia direct booking
//   when an L4B deal exists. Only real OpenGds API offers are direct.
//   Hotels with no agreement at all continue to book Expedia directly.
//
// Note: hotel.hasPriceAgreement is unreliable and intentionally NOT used.

const getReservationType = (offer) =>
  String(offer?.reservationType || "").toLowerCase();

const isOpenGdsOffer = (offer) => getReservationType(offer) === "opengds";

const isExpediaRapidOffer = (offer) =>
  getReservationType(offer) === "expediarapid";

// A BookingPortal offer is a reservation made through our booking portal
// (Salesforce). The backend emits these (e.g. RemoveExpediaOffersForBarAgreements
// rewrites Bar / PercentageOfBar / LowerThanBar agreement offers to
// ReservationType.BookingPortal with the agreed price). They are NOT
// direct-bookable — they always go through the request flow ("reservering
// aanvragen").
const isBookingPortalOffer = (offer) =>
  getReservationType(offer) === "bookingportal";

// An OpenRequest offer is an open request sent to Salesforce when there is no
// live availability. Like BookingPortal, it is a request-flow offer.
const isOpenRequestOffer = (offer) =>
  getReservationType(offer) === "openrequest";

// A synthesized agreed-price offer (no live availability, built from the
// agreement). The frontend legacy path flagged these `isSyntheticAgreedPrice`;
// the backend flags them `isSynthetic`. Treat either as synthetic.
const isSyntheticAgreedOffer = (offer) =>
  !!(offer?.isSyntheticAgreedPrice || offer?.isSynthetic);

// True when the offer must go through the request flow rather than a direct
// booking: BookingPortal and OpenRequest offers, and synthetic agreed offers.
// A real OpenGds offer is the only OpenGds-typed offer that is direct-bookable.
const isRequestFlowOffer = (offer) =>
  isBookingPortalOffer(offer) ||
  isOpenRequestOffer(offer) ||
  (isOpenGdsOffer(offer) && isSyntheticAgreedOffer(offer));

// --- Suppliers ----------------------------------------------------------

const getSupplier = (hotel, name) => {
  if (!hotel || !Array.isArray(hotel.suppliers)) return null;
  const target = String(name).toLowerCase();
  return (
    hotel.suppliers.find(
      (s) => String(s?.name || "").toLowerCase() === target
    ) || null
  );
};

const getOpenGdsSupplier = (hotel) => getSupplier(hotel, "OpenGds");

const getAgreedPricesSupplier = (hotel) =>
  getSupplier(hotel, "LookingforBooking agreed prices");

const getExpediaSupplier = (hotel) => getSupplier(hotel, "ExpediaRapid");

const supplierHasOffers = (supplier) =>
  !!(supplier && Array.isArray(supplier.offers) && supplier.offers.length > 0);

const hotelHasOpenGdsSupplierOffers = (hotel) =>
  supplierHasOffers(getOpenGdsSupplier(hotel));

const hotelHasAgreedPriceSupplierOffers = (hotel) =>
  supplierHasOffers(getAgreedPricesSupplier(hotel));

const hotelHasExpediaSupplierOffers = (hotel) =>
  supplierHasOffers(getExpediaSupplier(hotel));

// True when the hotel has any OpenGds relationship — the OpenGds supplier
// exists (real OpenGds offers may be available) OR the "LookingforBooking
// agreed prices" supplier exists (covers Bar agreements, which don't have
// the OpenGds supplier but still route through the request flow).
//
// This is the SINGLE check that determines whether a hotel is an "OpenGds
// hotel". Everything else flows from it: if true, only real OpenGds API
// offers are direct-bookable; all other offers go through the request flow.
const hotelIsOpenGds = (hotel) =>
  !!getOpenGdsSupplier(hotel) || !!getAgreedPricesSupplier(hotel);

// --- Agreements ---------------------------------------------------------

const getFirstAgreedPriceAgreement = (hotel) =>
  Array.isArray(hotel?.hotelAgreedPrices) && hotel.hotelAgreedPrices.length > 0
    ? hotel.hotelAgreedPrices[0]
    : null;

// True when the agreement applies to this room. Bar agreements have no
// expediaRoomId (hotel-wide), so they never match a specific room here.
//
// Match: room.internalRoomId === agreement.expediaRoomId.
// If the room has no internalRoomId, it cannot be matched to an agreement.
const roomMatchesAgreement = (room, agreement) => {
  if (!room || !agreement || !agreement.expediaRoomId) return false;
  if (!room.internalRoomId) return false;
  return String(room.internalRoomId) === String(agreement.expediaRoomId);
};

// --- CTA ----------------------------------------------------------------

// Whether the booking flow should be a "request reservation"
// ("reservering aanvragen") instead of a direct "book now" ("nu boeken"),
// based on the offers actually shown for the given room.
//
// Policy: when the hotel is an OpenGds hotel (has OpenGds supplier or
// agreed-prices supplier), only real OpenGds API offers are direct-
// bookable. Everything else goes through the request flow.
//
// This function is the FALLBACK used by HotelReservationForm.vue when the
// form is opened without router state. The primary signal is the backend-
// provided per-offer bookingMode, mapped onto the room in rooms.helper.js.
const shouldRequestReservation = (hotel, displayedOffers) => {
  if (!hotelIsOpenGds(hotel)) return false;
  const offers = displayedOffers || [];
  const hasRealOpenGdsOffer = offers.some(
    (o) => isOpenGdsOffer(o) && !isSyntheticAgreedOffer(o)
  );
  return !hasRealOpenGdsOffer;
};

export {
  getReservationType,
  isOpenGdsOffer,
  isExpediaRapidOffer,
  isBookingPortalOffer,
  isOpenRequestOffer,
  isRequestFlowOffer,
  getSupplier,
  getOpenGdsSupplier,
  getAgreedPricesSupplier,
  getExpediaSupplier,
  hotelHasOpenGdsSupplierOffers,
  hotelHasAgreedPriceSupplierOffers,
  hotelHasExpediaSupplierOffers,
  hotelIsOpenGds,
  getFirstAgreedPriceAgreement,
  roomMatchesAgreement,
  shouldRequestReservation
};
